/**
 * Validated environment access for the whole app.
 *
 * Every environment variable read anywhere under `src/` is declared here, in
 * one of two schemas:
 *
 *   - `server` — secrets and server-only config. Reading any of these from a
 *     browser bundle throws (`onInvalidAccess`), so a service-role key can
 *     never be smuggled into client code by a careless import.
 *   - `client` — the `NEXT_PUBLIC_*` vars. These are inlined into the browser
 *     bundle by Next.js, which is why they must be listed explicitly in
 *     `experimental__runtimeEnv` below (a dynamic `process.env[name]` lookup
 *     is not statically analysable and would come back `undefined`).
 *
 * Validation runs the moment this module is imported. `src/instrumentation.ts`
 * imports it from `register()`, so a deploy missing a production-required var
 * fails at server startup with every missing name in one message — instead of
 * failing open weeks later on the one code path that happened to need it.
 *
 * ## Required-in-production vs optional
 *
 * Being strict everywhere would brick local dev and preview deploys, which are
 * legitimately missing Stripe keys, PostHog keys, log-drain secrets and so on.
 * So `prodRequired()` only hard-requires a var on a real production deploy.
 * Everywhere else the var is optional and the existing call-site fallbacks
 * still apply. The inferred TypeScript type is `string | undefined` either
 * way, which keeps call sites honest.
 *
 * ## Escape hatch
 *
 * Set `SKIP_ENV_VALIDATION=1` to bypass validation entirely. This exists for
 * build containers and lint/typecheck passes that run without any real
 * secrets; it must never be set on a running server.
 */
import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

/**
 * True only on a real production deploy.
 *
 * Both halves matter. Preview deploys also run with `NODE_ENV=production`, so
 * keying off NODE_ENV alone would make every preview branch refuse to boot the
 * first time someone forgot to copy an optional secret into the preview
 * environment — `VERCEL_ENV` is the only signal that separates the two. And
 * keying off `VERCEL_ENV` alone would trip in tests that stub it to
 * `'production'` to exercise production code paths. Same definition as
 * `isProductionDeploy()` in `analytics/posthog-server.ts`, with `VERCEL_ENV`
 * absent (self-hosted, plain `next start`) treated as production.
 */
const IS_PRODUCTION_DEPLOY =
  process.env.NODE_ENV === 'production' &&
  (process.env.VERCEL_ENV === undefined || process.env.VERCEL_ENV === 'production');

/**
 * Required on production deploys, optional in dev/test/preview.
 *
 * The inferred type is `string | undefined` in both cases — deliberately. A
 * type claiming `string` while dev can legitimately produce `undefined` would
 * only push the failure into a `.length` crash somewhere downstream.
 */
function prodRequired<T extends z.ZodType>(schema: T) {
  return IS_PRODUCTION_DEPLOY ? schema : schema.optional();
}

/**
 * A Stripe price ID always starts with `price_`. Requiring the prefix catches
 * the common paste error of using a product id (`prod_…`) or a lookup key,
 * which Stripe would otherwise only reject at checkout with an opaque error.
 */
const stripePriceId = () =>
  z
    .string()
    .startsWith('price_', 'must be a Stripe price ID (starts with "price_")')
    .optional();

/** Price vars that must all be present once Stripe is switched on. */
const STRIPE_PRICE_VARS = [
  'STRIPE_PRICE_MONTHLY',
  'STRIPE_PRICE_ANNUAL',
  'STRIPE_PRICE_TEAM_MONTHLY',
  'STRIPE_PRICE_TEAM_ANNUAL',
] as const;

/** Render a Standard Schema issue path as a plain variable name. */
function issueName(issue: {
  readonly path?: ReadonlyArray<PropertyKey | { readonly key: PropertyKey }>;
}): string {
  if (!issue.path || issue.path.length === 0) return '(root)';
  return issue.path
    .map((segment) =>
      typeof segment === 'object' && segment !== null && 'key' in segment
        ? String(segment.key)
        : String(segment)
    )
    .join('.');
}

export const env = createEnv({
  // ---------------------------------------------------------------------------
  // Server-only — never reaches the browser bundle.
  // ---------------------------------------------------------------------------
  server: {
    // --- Supabase ------------------------------------------------------------
    /** Service-role key: bypasses RLS. Server-only, always. */
    SUPABASE_SERVICE_ROLE_KEY: prodRequired(z.string().min(1)),

    // --- Request authentication ----------------------------------------------
    /** HMAC secret for verifying SDK ingest signatures. */
    HMAC_SECRET: z.string().min(1).optional(),
    /** Preferred alias for HMAC_SECRET; `src/lib/ingest/hmac.ts` reads it first. */
    VERBAL_HMAC_SECRET: z.string().min(1).optional(),
    /** Shared secret Vercel Cron presents on scheduled job requests. */
    CRON_SECRET: prodRequired(z.string().min(1)),
    /** HMAC secret for signing outbound webhook payloads. */
    WEBHOOK_SIGNING_SECRET: z.string().min(1).optional(),
    /** Guards the insights seed endpoint. */
    SEED_SECRET: z.string().min(1).optional(),
    /** Shared secret on the Supabase log-drain ingest endpoint. */
    SUPABASE_LOG_DRAIN_SECRET: z.string().min(1).optional(),
    /** Shared secret on the Vercel log-drain ingest endpoint. */
    VERCEL_LOG_DRAIN_SECRET: z.string().min(1).optional(),
    /** Lets internal fan-out requests through Vercel deployment protection. */
    VERCEL_AUTOMATION_BYPASS_SECRET: z.string().min(1).optional(),
    /** Default API key picked up by the bundled Node SDK. */
    PROMPTER_API_KEY: z.string().min(1).optional(),
    /** Comma-separated allowlist of site-admin emails (gates `/admin/*`). */
    ADMIN_EMAILS: z.string().optional(),

    // --- LLM -----------------------------------------------------------------
    /**
     * Vercel AI Gateway key — every LLM call in the app routes through it.
     * Auto-provisioned via OIDC on Vercel; required on production so a missing
     * key surfaces at boot rather than as a 500 on the first coaching run.
     */
    AI_GATEWAY_API_KEY: prodRequired(z.string().min(1)),

    // --- Email ---------------------------------------------------------------
    /** Resend API key for transactional email. */
    RESEND_API_KEY: z.string().min(1).optional(),
    /** Sender address; must match a verified Resend domain. */
    EMAIL_FROM: z.string().min(1).optional(),
    /** Deprecated HTTP email hook — superseded by direct Resend sends. */
    NOTIFICATION_EMAIL_ENDPOINT: z.string().optional(),

    // --- OAuth ---------------------------------------------------------------
    /** Google OAuth client ID for social login. */
    GOOGLE_OAUTH_CLIENT_ID: z.string().min(1).optional(),

    // --- Stripe --------------------------------------------------------------
    /** Stripe secret key. Its presence switches billing on (see below). */
    STRIPE_SECRET_KEY: z.string().min(1).optional(),
    /** Signing secret for incoming Stripe webhooks. */
    STRIPE_WEBHOOK_SECRET: z.string().min(1).optional(),
    STRIPE_PRICE_MONTHLY: stripePriceId(),
    STRIPE_PRICE_ANNUAL: stripePriceId(),
    STRIPE_PRICE_TEAM_MONTHLY: stripePriceId(),
    STRIPE_PRICE_TEAM_ANNUAL: stripePriceId(),

    // --- Feature flags / ops toggles -----------------------------------------
    /** `'true'` stores prompt content unencrypted. Local development only. */
    SKIP_ENCRYPTION: z.enum(['true', 'false']).optional(),
    /** Enables the retention cron's destructive prompt purge. */
    RETENTION_PROMPT_PURGE_ENABLED: z.string().optional(),
    /** Enables the ingest enrichment outbox. */
    ENRICHMENT_OUTBOX_ENABLED: z.string().optional(),
    /** Kill switch for live parallelism detection. */
    PARALLELISM_LIVE_DISABLED: z.string().optional(),
    /** Emits timing logs from the live parallelism detector. */
    PARALLELISM_LIVE_TIMING: z.string().optional(),

    // --- CLI release channel -------------------------------------------------
    /** Newest published @getverbal/cli version, served by /api/v1/cli/version. */
    CLI_LATEST_VERSION: z.string().optional(),
    /** Oldest @getverbal/cli version still allowed to talk to the API. */
    CLI_MIN_VERSION: z.string().optional(),
    /** Message shown to CLI users on an outdated version. */
    CLI_UPDATE_MESSAGE: z.string().optional(),

    // --- Base URLs (server-side variants) ------------------------------------
    // NOTE: six overlapping base-URL vars exist (APP_URL, NEXT_PUBLIC_APP_URL,
    // NEXT_PUBLIC_BASE_URL, NEXT_PUBLIC_SITE_URL, NEXT_PUBLIC_VERBAL_BASE_URL,
    // VERCEL_URL). Consolidating them is deliberately out of scope here — the
    // blast radius is far larger than this change should carry.
    /** Absolute app origin; server-side fallback for NEXT_PUBLIC_APP_URL. */
    APP_URL: z.string().optional(),

    // --- Vercel platform (injected automatically) ----------------------------
    /** Set to `'1'` on any Vercel deployment. */
    VERCEL: z.string().optional(),
    /** Which Vercel environment this deployment belongs to. */
    VERCEL_ENV: z.enum(['production', 'preview', 'development']).optional(),
    /** Deployment host without protocol, e.g. `verbal-abc123.vercel.app`. */
    VERCEL_URL: z.string().optional(),
    /** Commit SHA of the deployment — reported to PostHog as the release. */
    VERCEL_GIT_COMMIT_SHA: z.string().optional(),
    /** Git branch of the deployment. */
    VERCEL_GIT_COMMIT_REF: z.string().optional(),
  },

  // ---------------------------------------------------------------------------
  // Client — inlined into the browser bundle. Never put a secret here.
  // ---------------------------------------------------------------------------
  client: {
    /** Supabase project URL. Required everywhere; nothing works without it. */
    NEXT_PUBLIC_SUPABASE_URL: z.url(),
    /** Supabase anon key — RLS-scoped, safe to expose. */
    NEXT_PUBLIC_SUPABASE_ANON_KEY: z.string().min(1),

    /**
     * PostHog project key and host. Required on production: without them the
     * server-side client silently drops every captured exception, which is how
     * production errors went unnoticed before (see `analytics/posthog-server.ts`).
     */
    NEXT_PUBLIC_POSTHOG_KEY: prodRequired(z.string().min(1)),
    NEXT_PUBLIC_POSTHOG_HOST: prodRequired(z.url()),

    /** Base URLs — see the consolidation note in the server block above. */
    NEXT_PUBLIC_BASE_URL: z.url().optional(),
    NEXT_PUBLIC_APP_URL: z.url().optional(),
    NEXT_PUBLIC_SITE_URL: z.url().optional(),

    /** Stripe publishable key and client-safe copies of the price IDs. */
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: z.string().min(1).optional(),
    NEXT_PUBLIC_STRIPE_PRICE_MONTHLY: stripePriceId(),
    NEXT_PUBLIC_STRIPE_PRICE_ANNUAL: stripePriceId(),
    NEXT_PUBLIC_STRIPE_PRICE_TEAM_MONTHLY: stripePriceId(),
    NEXT_PUBLIC_STRIPE_PRICE_TEAM_ANNUAL: stripePriceId(),

    /** Prefilled values shown in the onboarding setup guides. */
    NEXT_PUBLIC_VERBAL_API_KEY: z.string().optional(),
    NEXT_PUBLIC_VERBAL_USER_ID: z.string().optional(),
    NEXT_PUBLIC_VERBAL_ORG_ID: z.string().optional(),
    NEXT_PUBLIC_VERBAL_BASE_URL: z.string().optional(),
  },

  // ---------------------------------------------------------------------------
  // Shared — readable from both runtimes.
  // ---------------------------------------------------------------------------
  shared: {
    NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),
  },

  /**
   * Only client and shared vars need listing: Next.js ≥ 13.4.4 no longer
   * statically analyses server-side `process.env`, so server vars are read at
   * runtime. Client vars must appear as literal member expressions for the
   * bundler to inline them.
   */
  experimental__runtimeEnv: {
    NODE_ENV: process.env.NODE_ENV,
    NEXT_PUBLIC_SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL,
    NEXT_PUBLIC_SUPABASE_ANON_KEY: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
    NEXT_PUBLIC_POSTHOG_KEY: process.env.NEXT_PUBLIC_POSTHOG_KEY,
    NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    NEXT_PUBLIC_BASE_URL: process.env.NEXT_PUBLIC_BASE_URL,
    NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL,
    NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
    NEXT_PUBLIC_STRIPE_PRICE_MONTHLY: process.env.NEXT_PUBLIC_STRIPE_PRICE_MONTHLY,
    NEXT_PUBLIC_STRIPE_PRICE_ANNUAL: process.env.NEXT_PUBLIC_STRIPE_PRICE_ANNUAL,
    NEXT_PUBLIC_STRIPE_PRICE_TEAM_MONTHLY: process.env.NEXT_PUBLIC_STRIPE_PRICE_TEAM_MONTHLY,
    NEXT_PUBLIC_STRIPE_PRICE_TEAM_ANNUAL: process.env.NEXT_PUBLIC_STRIPE_PRICE_TEAM_ANNUAL,
    NEXT_PUBLIC_VERBAL_API_KEY: process.env.NEXT_PUBLIC_VERBAL_API_KEY,
    NEXT_PUBLIC_VERBAL_USER_ID: process.env.NEXT_PUBLIC_VERBAL_USER_ID,
    NEXT_PUBLIC_VERBAL_ORG_ID: process.env.NEXT_PUBLIC_VERBAL_ORG_ID,
    NEXT_PUBLIC_VERBAL_BASE_URL: process.env.NEXT_PUBLIC_VERBAL_BASE_URL,
  },

  /**
   * `.env.local.example` and `vercel env pull` both produce blank assignments
   * (`STRIPE_SECRET_KEY=`) rather than absent keys. Without this, `""` would
   * satisfy `.optional()` and then fail `.min(1)` / `.startsWith("price_")`,
   * so a perfectly valid Stripe-disabled local env would refuse to boot.
   */
  emptyStringAsUndefined: true,

  /**
   * Validation is a *runtime* gate, not a build gate.
   *
   * `next build` executes server modules while collecting page data, so a
   * build box that legitimately has no secrets — CI's E2E job supplies only
   * the two Supabase vars — would fail to compile rather than fail to boot.
   * That is the wrong signal in the wrong place: the deploy that matters is
   * checked by `register()` in `src/instrumentation.ts`, which runs on the
   * server that actually holds the environment.
   *
   * `NEXT_PHASE` is set to `phase-production-build` by Next.js for the whole
   * build, including the page-data workers, and is absent at runtime.
   * `SKIP_ENV_VALIDATION` is the manual override for anything else (container
   * builds, one-off tooling). Never set it on a running server — it turns the
   * whole module into an unvalidated passthrough.
   */
  skipValidation:
    process.env.NEXT_PHASE === 'phase-production-build' ||
    process.env.SKIP_ENV_VALIDATION === '1' ||
    process.env.SKIP_ENV_VALIDATION === 'true',

  /**
   * Cross-field rules a per-variable schema cannot express.
   *
   * Stripe is optional overall (local dev runs without it), but a HALF
   * configured Stripe is worse than none: `resolveTierFromPrice` and
   * `isTeamPrice` both resolve against the price vars, so a missing price ID
   * means a customer can complete checkout and be charged while the webhook
   * refuses to grant any tier — a real charge for no product access.
   */
  createFinalSchema: (shape, isServer) =>
    z.object(shape).superRefine((value, ctx) => {
      if (!isServer) return;
      const parsed = value as Record<string, string | undefined>;

      if (IS_PRODUCTION_DEPLOY && !parsed['HMAC_SECRET'] && !parsed['VERBAL_HMAC_SECRET']) {
        ctx.addIssue({
          code: 'custom',
          path: ['HMAC_SECRET'],
          message: 'HMAC_SECRET or VERBAL_HMAC_SECRET is required on production deployments',
        });
      }

      if (!parsed['STRIPE_SECRET_KEY']) return;

      for (const key of STRIPE_PRICE_VARS) {
        if (!parsed[key]) {
          ctx.addIssue({
            code: 'custom',
            path: [key],
            message:
              'required when STRIPE_SECRET_KEY is set — checkout would charge customers without granting a tier',
          });
        }
      }

      if (!parsed['STRIPE_WEBHOOK_SECRET']) {
        ctx.addIssue({
          code: 'custom',
          path: ['STRIPE_WEBHOOK_SECRET'],
          message:
            'required when STRIPE_SECRET_KEY is set — webhook events cannot be verified without it',
        });
      }
    }),

  onValidationError: (issues) => {
    const names = [...new Set(issues.map((issue) => issueName(issue)))];
    const detail = issues.map((issue) => `  - ${issueName(issue)}: ${issue.message}`).join('\n');

    throw new Error(
      `Invalid environment variables (${names.length}): ${names.join(', ')}\n${detail}\n` +
        'Set them in .env.local (see .env.local.example) or in the Vercel project settings.'
    );
  },

  onInvalidAccess: (variable) => {
    throw new Error(
      `Attempted to read server-only environment variable "${variable}" from client code. ` +
        'Move the read into a server component, route handler, or server action.'
    );
  },
});
